import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ShieldAlert, Flame, HeartPulse, AlertTriangle, MapPin, Loader2, Car, TrafficCone, Camera } from "lucide-react";
import { toast } from "sonner";
import AlertImagePicker from "./AlertImagePicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const typeConfig = {
  security: { titleKey: "create_alert.title_security", icon: ShieldAlert, color: "text-red-500", bg: "bg-red-50 border-red-200" },
  fire: { titleKey: "create_alert.title_fire", icon: Flame, color: "text-orange-500", bg: "bg-orange-50 border-orange-200" },
  medical: { titleKey: "create_alert.title_medical", icon: HeartPulse, color: "text-blue-500", bg: "bg-blue-50 border-blue-200" },
  accident: { titleKey: "create_alert.title_accident", icon: Car, color: "text-yellow-600", bg: "bg-yellow-50 border-yellow-200" },
  banditry: { titleKey: "create_alert.title_banditry", icon: AlertTriangle, color: "text-rose-900", bg: "bg-rose-50 border-rose-200" },
  ecaution: { titleKey: "create_alert.title_ecaution", icon: TrafficCone, color: "text-amber-600", bg: "bg-amber-50 border-amber-200" },
  location_share: { titleKey: "create_alert.title_location_share", icon: MapPin, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
};

const CAUTION_PRESETS = [
  "Men at work, slow down",
  "Collapsed bridge ahead",
  "Bumps ahead",
  "Railway crossing ahead",
  "Pothole ahead",
  "Accident on road",
  "Flooded road ahead",
  "Fallen tree on road",
  "Livestock on road",
  "Police checkpoint ahead",
];

function GPSInfo({ userLocation, bgClass, t }) {
  return (
    <div className={`flex items-center gap-2 p-3 border rounded-sm ${bgClass}`}>
      <MapPin className="w-4 h-4 text-gray-600 shrink-0" />
      {userLocation ? (
        <p className="text-xs font-body text-gray-700">
          {t("create_alert.gps_ready")} {userLocation.latitude.toFixed(6)}, {userLocation.longitude.toFixed(6)}
        </p>
      ) : (
        <p className="text-xs font-body text-red-600">{t("create_alert.gps_unavailable")}</p>
      )}
    </div>
  );
}

export default function CreateAlertDialog({ open, onClose, alertType, userLocation, onCreated }) {
  const { t } = useTranslation();
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const config = typeConfig[alertType] || typeConfig.security;
  const Icon = config.icon;

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error(t("create_alert.err_describe"));
      return;
    }
    if (!userLocation) {
      toast.error(t("create_alert.err_location"));
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/alerts`,
        {
          alert_type: alertType,
          description: description.trim(),
          latitude: userLocation.latitude,
          longitude: userLocation.longitude,
          images: images.length > 0 ? images : null,
        },
        { withCredentials: true }
      );
      setDescription("");
      setImages([]);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("create_alert.err_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md rounded-sm border border-gray-200" data-testid="create-alert-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <Icon className={`w-5 h-5 ${config.color}`} />
            {t(config.titleKey)}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <GPSInfo userLocation={userLocation} bgClass={config.bg} t={t} />

          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-1.5 block">
              {alertType === "ecaution" ? t("create_alert.caution_required") : t("create_alert.description_required")}
            </Label>
            {alertType === "ecaution" && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="caution-presets">
                {CAUTION_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setDescription(preset)}
                    className={`text-[11px] rounded-sm px-2 py-1 border font-body transition ${description === preset ? "bg-amber-600 text-white border-amber-700" : "bg-white text-gray-700 border-gray-300 hover:border-amber-400 hover:text-amber-700"}`}
                    data-testid={`preset-${preset.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {preset}
                  </button>
                ))}
              </div>
            )}
            <Textarea
              data-testid="alert-description-input"
              placeholder={alertType === "ecaution" ? t("create_alert.caution_placeholder") : t("create_alert.description_placeholder")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="rounded-sm border-gray-300 focus:ring-black font-body text-sm resize-none"
              rows={3}
            />
          </div>

          {alertType !== "location_share" && (
            <div data-testid="composer-image-picker">
              <Label className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-1.5 block flex items-center gap-1.5">
                <Camera className="w-3.5 h-3.5" /> {t("create_alert.photos_label")}
              </Label>
              <AlertImagePicker images={images} onChange={setImages} max={3} disabled={submitting} />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm font-body" data-testid="cancel-alert-btn">
            {t("create_alert.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !userLocation}
            className="bg-black hover:bg-gray-800 text-white rounded-sm font-body"
            data-testid="submit-alert-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {t("create_alert.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
